import { nanoid } from "nanoid"
import { withTypesenseKeyRetry } from "@/utils/typesenseRetry"
import Logger from "@/utils/Logger"
import { typesenseApiKeyCacheKey } from "@/utils/typesenseApiKeyCacheKey"

export async function useJobData(jobId: string) {
  const { $api } = useNuxtApp()
  const { t } = useI18n()
  const { tenantId, jobShopId } = useHeaderStore()
  const { data: typesenseApiKey } = useNuxtData(typesenseApiKeyCacheKey(jobShopId))
  const { public: { typesenseUrl } } = useRuntimeConfig()
  const { refreshTypesenseApiKey } = useTypesenseKeyRefresh()

  if (!typesenseApiKey.value)
    throw new Error("Typesense API key is missing")

  const requestId = nanoid()
  const contextString = `job data for jobId: "${jobId}", tenantId: "${tenantId}", jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const {
    data: job,
    status: jobDataStatus,
    error: jobDataError,
  } = await useAsyncData(`jobData-${jobId}`, () => withTypesenseKeyRetry(
    key => $api.job.getJobById(tenantId, jobShopId, jobId, key, typesenseUrl),
    typesenseApiKey.value,
    refreshTypesenseApiKey,
  ))

  if (import.meta.server) {
    if (!jobDataError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = jobDataError.value?.data?.errors?.[0]?.message || jobDataError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, jobDataError.value)
    }
  }

  if (jobDataError.value) {
    if (jobDataError.value.statusCode === 404) {
      // A Typesense miss is ambiguous: the offer might be deactivated (410 Gone,
      // which crawlers deindex quickly) or it may never have existed (404). Ask
      // offer-service to disambiguate — getOfferAvailability never throws and
      // returns undefined on transient failures, so we safely default to 404.
      const availabilityStatus = await $api.offer.getOfferAvailability(tenantId, jobId)
      const gone = availabilityStatus === 410

      if (import.meta.server)
        Logger(requestId).info(`Offer availability for ${contextString} returned ${availabilityStatus ?? "no status"} — serving ${gone ? "410 Gone" : "404"}.`)

      if (gone) {
        throw createError({
          statusCode: 410,
          statusMessage: t("error-messages.job-no-longer-available"),
        })
      }

      throw createError({
        statusCode: 404,
        statusMessage: t("error-messages.page-not-found"),
      })
    }
    else {
      throw createError({
        statusCode: jobDataError.value.statusCode,
        statusMessage: t("error-messages.general"),
      })
    }
  }

  return { job, jobDataStatus, jobDataError }
}
